/**
 * The « voir comme » headers — the eye menu and the speciality picker, on the wire.
 *
 * ONE module, because there were two: `services/api.ts` for every call going through the API
 * service, and a hand-rolled copy in `routes/configuration.tsx` for the two raw `fetch()` calls
 * (upload, SSE pipeline) that bypass it. The copies had already drifted — one announced the
 * previewed role for « Vue globale » and the other did not — and a preview that is honoured on
 * some requests of a page and not others is worse than no preview at all.
 *
 * Three headers, and what each one names:
 *
 *  - `X-View-As-User-Id` — WHO. The previewed doctor's account; the backend redirects every
 *    owner-scoped read to them and reads their speciality off their own row.
 *  - `X-View-As-Specialite` — WHICH SPECIALITY, when the reader CHOSE one from the sidebar. It is
 *    deliberately not sent while previewing a doctor: an identity dominates a perimeter, and the
 *    previewed doctor's speciality is theirs to state, off their own row. Sending one alongside a
 *    doctor made the dashboard's nominative cache serve the whole speciality under a banner
 *    promising one practitioner's dossiers. The choice is kept in the store meanwhile, and applies
 *    again the moment the preview ends.
 *  - `X-View-As-Role` — WHICH ROLE is being simulated. Read only by display gates, so it is sent
 *    for every previewed role, « global » included: that persona has no "me" to be placed against.
 */
import { usePreviewView } from "@app/zustand/preview-view";

export const VIEW_AS_USER_ID_HEADER = "X-View-As-User-Id";
export const VIEW_AS_SPECIALITE_HEADER = "X-View-As-Specialite";
export const VIEW_AS_ROLE_HEADER = "X-View-As-Role";

/** The headers describing the current preview. Empty object when nothing is being previewed. */
export function viewAsHeaders(): Record<string, string> {
  const { previewRole, previewMedecin, previewSpecialite } =
    usePreviewView.getState();
  const headers: Record<string, string> = {};
  if (previewRole === "medecin" && previewMedecin?.id) {
    headers[VIEW_AS_USER_ID_HEADER] = previewMedecin.id;
  }
  if (previewSpecialite && previewRole !== "medecin") {
    headers[VIEW_AS_SPECIALITE_HEADER] = previewSpecialite;
  }
  if (previewRole) {
    headers[VIEW_AS_ROLE_HEADER] = previewRole;
  }
  return headers;
}

/**
 * A string identifying WHOSE platform is on screen — stable while the preview does not change.
 *
 * Every fetch whose answer depends on the preview must re-run when this changes, and anything it
 * has already displayed belongs to the previous reader: the eye is not a filter, it is a change of
 * identity, so stale figures are not "not fresh yet" but someone else's.
 */
export function viewAsKey(): string {
  const { previewRole, previewMedecin, previewSpecialite } =
    usePreviewView.getState();
  return `${previewRole ?? ""}|${previewMedecin?.id ?? ""}|${previewSpecialite ?? ""}`;
}

/** Reactive `viewAsKey` — use it as an effect dependency. */
export function useViewAsKey(): string {
  const previewRole = usePreviewView((state) => state.previewRole);
  const previewMedecinId = usePreviewView((state) => state.previewMedecin?.id);
  const previewSpecialite = usePreviewView((state) => state.previewSpecialite);
  return `${previewRole ?? ""}|${previewMedecinId ?? ""}|${previewSpecialite ?? ""}`;
}
